import React from 'react'
import { ConfirmationModal, PageTitle } from 'src/components'
import { useStyles } from './styles'
import { useBirthRegistration } from './hooks/useBirthRegistration'
import ROUTES from 'src/constants/routes'
import { DiagnosisModal } from '../components/DiagnosisModal'
import { LoadingOverlay } from 'src/components/LoadingOverlay'
import { BirthDataSection } from './BirthDataSection'
import { ReproductionDetailsSection } from './ReproductionDetailsSection'
import { OffspringSection } from './OffspringSection'
import { ActionsSection } from './ActionsSection'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { PregnancyDiagnosisParams } from 'src/domain/coverage-process'
import { VALIDATIONS } from 'src/@types/validations'

export interface BirthRegistrationParams extends PregnancyDiagnosisParams {
  dataFim?: string
}

export const defaultFormValues: any = {
  idAnimal: '',
  dataDiagnostico: new Date().toISOString(),
  tipoReproducao: 'monta',
  touro: '',
  tipoParto: 'normal',
  dataCobertura: new Date().toISOString(),
  dataInicial: new Date().toISOString(),
  dataParto: new Date().toISOString(),
  dataFim: ''
}
export const BirthRegistration: React.FC = () => {
  const methods = useForm<BirthRegistrationParams>({
    resolver: yupResolver(VALIDATIONS.AnimalBirth),
    defaultValues: defaultFormValues
  })

  return (
    <FormProvider {...methods}>
      <BirthRegistrationComponent />
    </FormProvider>
  )
}

export const BirthRegistrationComponent: React.FC = () => {
  const style = useStyles()

  const {
    selectedAnimal,
    savedBirths,
    isDiagnosisModalOpen,
    useDateInterval,
    isLoadingAnimal,
    handleSelectAnimal,
    handleToggleDateInterval,
    handleCloseDiagnosisModal,
    handleOpenDiagnosisModal,
    handleSaveBirths,
    handleRemoveBirth,
    handleSaveAll,
    showBirthWarningModal,
    handleCancelWarningDiagnosisModal,
    handleConfirmWarningDiagnosisModal,
    isSaving
  } = useBirthRegistration()

  const { watch, setError } = useFormContext<BirthRegistrationParams>()

  const selectedAnimalId = watch('idAnimal')
  const tipoReproducao = watch('tipoReproducao')
  const dataParto = watch('dataParto')
  const dataCobertura = watch('dataCobertura')
  const dataInicial = watch('dataInicial')

  const today = new Date().toISOString().split('T')[0]

  const validateDateNotFuture = (value: string, fieldName: string, fieldLabel: string) => {
    const selectedDate = new Date(value)
    const todayDate = new Date(today)

    if (selectedDate > todayDate) {
      setError(fieldName as any, { message: `${fieldLabel} não pode ser superior à data de hoje` })
      return false
    }
    return true
  }

  return (
    <>
      <LoadingOverlay isVisible={isLoadingAnimal} />
      <div className={style.propertiesHeader}>
        <PageTitle
          title='Cadastro de Nascimento'
          className={style.propertiesTitle}
          breadcrumbLink={{
            to: ROUTES.REPRODUCTION.ROOT,
            text: 'Reprodução'
          }}
        />
      </div>
      <div className={style.propertiesContainer}>
        <h2 className={style.sectionTitle}>Dados do Nascimento</h2>
        <BirthDataSection
          selectedAnimalId={selectedAnimalId}
          isLoadingAnimal={isLoadingAnimal}
          dataParto={dataParto!}
          handleSelectAnimal={handleSelectAnimal}
          validateDateNotFuture={validateDateNotFuture}
        />
        {selectedAnimalId && (
          <div className={style.reproductionDiagnosisLayout}>
            <ReproductionDetailsSection
              useDateInterval={useDateInterval}
              isLoadingAnimal={isLoadingAnimal}
              handleToggleDateInterval={handleToggleDateInterval}
              validateDateNotFuture={validateDateNotFuture}
            />
            <OffspringSection
              tipoReproducao={tipoReproducao}
              savedBirths={savedBirths}
              isLoadingAnimal={isLoadingAnimal}
              handleOpenDiagnosisModal={handleOpenDiagnosisModal}
              handleRemoveBirth={handleRemoveBirth}
            />
          </div>
        )}
        {selectedAnimalId && savedBirths.length > 0 && (
          <ActionsSection handleSaveAll={handleSaveAll} isSaving={isSaving} savedBirths={savedBirths} />
        )}
      </div>
      <DiagnosisModal
        initView={'animalForm'}
        isOpen={isDiagnosisModalOpen}
        onClose={handleCloseDiagnosisModal}
        motherAnimalId={selectedAnimalId || ''}
        motherAnimalName={selectedAnimal?.nome || ''}
        fatherAnimalId={watch('touro') || ''}
        diagnosisDate={dataParto || ''}
        reproductionType={tipoReproducao}
        reproductionDate={useDateInterval ? dataInicial : dataCobertura || ''}
        onSaveAll={handleSaveBirths}
        existingBirths={savedBirths}
      />
      <ConfirmationModal
        isOpen={showBirthWarningModal}
        title='Atenção'
        message='Não existe uma gestação em andamento para o animal selecionado, uma nova gestação será criada.'
        confirmText='Sim, continuar'
        cancelText='Não, cancelar'
        onConfirm={handleConfirmWarningDiagnosisModal}
        onCancel={handleCancelWarningDiagnosisModal}
      />
    </>
  )
}
